import React from "react";
import {
  Terminal,
  GitBranch,
  Zap,
  ShieldCheck,
  Clock,
  Activity,
  Cpu
} from "lucide-react";

import Navigation from "../components/Navigation";
import FooterSection from "../components/FooterSection";
import "./Updates.css";

const updatesData = [
  {
    version: "v2.1.0",
    date: "April 17, 2026",
    title: "Clinical Analysis & Platform Expansion",
    type: "Major Release",
    description: "Introduced advanced clinical dashboards and expanded system modules.",
    changes: [
      "New Clinical Analysis dashboard",
      "Live + History vitals tracking",
      "Added Cookie Policy & Partnerships pages",
      "Improved Features & Technical pages",
      "Redesigned 404 page",
      "Optimized CSS architecture"
    ],
    icon: Activity
  },
  {
    version: "v2.0.0",
    date: "April 05, 2026",
    title: "UI Revamp & Dashboard Upgrade",
    type: "Major Release",
    description: "Complete redesign of UI and dashboard experience.",
    changes: [
      "New HUD-based UI design",
      "Improved navigation system",
      "Better responsiveness",
      "Enhanced patient dashboard",
      "Dark mode optimization"
    ],
    icon: Zap
  },
  {
    version: "v1.6.0",
    date: "March 20, 2026",
    title: "Performance Optimization",
    type: "Update",
    description: "Improved speed and system performance.",
    changes: [
      "Reduced loading time",
      "Optimized Firebase queries",
      "Improved API response time",
      "Better memory handling"
    ],
    icon: Cpu
  },
  {
    version: "v1.5.0",
    date: "March 05, 2026",
    title: "Identity System Upgrade",
    type: "Major Release",
    description: "Improved user identity and authentication system.",
    changes: [
      "Separate caregiver database",
      "Improved login system",
      "New caregiver dashboard",
      "Doctor authorization system"
    ],
    icon: Zap
  },
  {
    version: "v1.3.0",
    date: "February 18, 2026",
    title: "Notification & Alerts System",
    type: "Feature Update",
    description: "Added real-time alerts and notification system.",
    changes: [
      "Emergency alerts added",
      "Notification center UI",
      "Real-time updates",
      "Alert history tracking"
    ],
    icon: Activity
  },
  {
    version: "v1.1.5",
    date: "February 12, 2026",
    title: "Security Update",
    type: "Security Update",
    description: "Enhanced platform security and privacy.",
    changes: [
      "AES-256 encryption",
      "Multi-factor authentication",
      "Secure data handling",
      "Improved audit logs"
    ],
    icon: ShieldCheck
  },
  {
    version: "v1.1.0",
    date: "January 25, 2026",
    title: "Doctor & Patient Modules",
    type: "Feature Update",
    description: "Expanded doctor and patient features.",
    changes: [
      "Doctor dashboard added",
      "Patient profile system",
      "Vitals display UI",
      "Basic appointment system"
    ],
    icon: Activity
  },
  {
    version: "v1.0.5",
    date: "January 10, 2026",
    title: "Initial Improvements",
    type: "Update",
    description: "Bug fixes and UI improvements.",
    changes: [
      "Fixed login bugs",
      "Improved UI layout",
      "Better form validation",
      "Performance tweaks"
    ],
    icon: Cpu
  },
  {
    version: "v1.0.0",
    date: "December 01, 2025",
    title: "Platform Launch",
    type: "Initial Release",
    description: "First release of Medico.in.",
    changes: [
      "Vitals monitoring dashboard",
      "Doctor & patient system",
      "Sensor integration",
      "Cloud database setup"
    ],
    icon: GitBranch
  }
];

const Updates = () => {
  return (
    <div className="up-portal">
      <Navigation />

      <div className="up-grid-overlay"></div>
      <div className="up-scanline"></div>

      <main className="up-content">
        <header className="up-hero">
          <div className="up-hud-badge">
            <Terminal size={14} />
            <span>PLATFORM_UPDATES</span>
          </div>

          <h1>
            System <span className="text-gradient">Updates</span>
          </h1>

          <p>Track all improvements and releases of Medico.in.</p>
        </header>

        <section className="up-timeline">
          {updatesData.map((update) => {
            const Icon = update.icon;

            return (
              <div key={update.version} className="up-timeline-item">
                <div className="up-timeline-marker"></div>

                <div className="up-card">
                  <div className="up-card-header">
                    <div className="up-version-tag">
                      <span className="up-v-num">{update.version}</span>

                      <span
                        className={`up-type-badge ${update.type
                          .toLowerCase()
                          .replace(/\s+/g, "-")}`}
                      >
                        {update.type}
                      </span>
                    </div>

                    <h3 className="up-card-title">{update.title}</h3>

                    <div className="up-date">
                      <Clock size={14} />
                      <span>{update.date}</span>
                    </div>
                  </div>

                  <div className="up-card-body">
                    <p>{update.description}</p>

                    <div className="up-changes-list">
                      <h4>Updates:</h4>

                      <ul>
                        {update.changes.map((change, i) => (
                          <li key={i}>
                            <div className="up-bullet"></div>
                            <span>{change}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </section>
      </main>

      <FooterSection />
    </div>
  );
};

export default Updates;